import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,  
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  Autocomplete,
} from '@mui/material';
import { FaTimes } from 'react-icons/fa';
import { useEffect, useState, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { paymentOption } from '../../Helpers/Constant/CategoryContant';
import DateRangeFilter from "../DateRangeFilter";
import {
  useLazyGetCustomersQuery,
} from "../../Services/CustomerService";

interface CustomerFilters {
  startDate?: string; // YYYY-MM-DD
  endDate?: string;   // YYYY-MM-DD
  status: string;
  search: string;
}

interface CustomerSlidingFilterProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: CustomerFilters) => void;
  onClearFilters: () => void;
  filters: CustomerFilters;
  onFilterChange: (filterName: keyof CustomerFilters, value: any) => void;
}

const CustomerSlidingFilter: React.FC<CustomerSlidingFilterProps> = ({
  open,
  onClose,
  onApplyFilters,
  onClearFilters,
  filters,
  onFilterChange,
}) => {
  const theme = useTheme();
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);
  // No default dates on open; keep independent start/end values as provided by parent

    const [
      getCustomersTrigger,
      { data: customerData, isLoading: customerLoading, error: customerError },
    ] = useLazyGetCustomersQuery();


    // Pagination state for autocomplete
    const [currentPage, setCurrentPage] = useState(1);
    const [allCustomers, setAllCustomers] = useState<any[]>([]);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Initial data fetch for autocomplete options
    useEffect(() => {
      setCurrentPage(1);
      setAllCustomers([]);
      setHasMoreData(true);
    }, []);

    // Debounced search effect
    useEffect(() => {
      const handler = setTimeout(() => {
        if (searchTerm.trim()) {
          setCurrentPage(1);
          setAllCustomers([]);
          setHasMoreData(true);
          
          getCustomersTrigger({
            filters: [],
            page: 1,
            pageSize: 50,
            sortBy: "createdAt",
            sortOrder: "desc",
            search: searchTerm.trim()
          });
        }
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    }, [searchTerm, getCustomersTrigger]);

    // Update allOrders when new data arrives
    useEffect(() => {
      if (customerData?.data) {
        if (currentPage === 1) {
          setAllCustomers(customerData.data);
        } else {
          setAllCustomers(prev => [...prev, ...customerData.data]);
        }
        
        // Check if there's more data
        const totalPages = Math.ceil(customerData.totalRecords / 20);
        setHasMoreData(currentPage < totalPages);
        setIsLoadingMore(false);
      }
    }, [customerData, currentPage]);

    

    // Function to load more data
    const loadMoreData = useCallback(() => {
      if (!isLoadingMore && hasMoreData && !customerLoading) {
        setIsLoadingMore(true);
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        
        getCustomersTrigger({
          filters: [], // Empty filters to get all records for autocomplete
          page: nextPage,
          pageSize:50,
          sortBy: "createdAt",
          sortOrder: "desc",
          search: '' // Empty search to get all records
        });

      }
    }, [currentPage, hasMoreData, isLoadingMore, customerLoading, getCustomersTrigger]);

    // Function to load all records on focus
    const loadAllRecords = useCallback(() => {
      if (!customerLoading && !isLoadingMore) {
        setCurrentPage(1);
        setAllCustomers([]);
        setHasMoreData(true);
        
        // Fetch with empty search to get all record
        getCustomersTrigger({
          page: 1,
          pageSize: 50,
          filters: [],
          search: '',
          sortBy: 'createdAt',
          sortOrder: 'desc'
        });
      }
    }, [customerLoading, isLoadingMore, getCustomersTrigger]);


    // Sync searchTerm with filters.search only when filters are explicitly cleared
    useEffect(() => {
      setSearchTerm(filters.search || '');
    }, [filters.search]);

    // Extract search options from all loaded order data
    const searchOptions = React.useMemo(() => {
      if (!allCustomers.length) return [];
      
      const options: string[] = [];
      
      allCustomers.forEach((customer: any) => {        
        // Add customer name (separate)
        if (customer?.firstName && customer?.lastName) {
          const fullName = `${customer.firstName} ${customer?.lastName} - ${customer?.email}`;
          if (!options.includes(fullName)) {
            options.push(fullName);
          }
        }
        
      });
      
      return options.sort();
    }, [allCustomers]);

    // Function to determine what type of search value to send
    const getSearchValue = (searchTerm: string) => {
      if (!searchTerm) return '';
      
      // Check if it's the concatenated format "Name - Email"
      if (searchTerm.includes(' - ')) {
        const parts = searchTerm.split(' - ');
        if (parts.length === 2) {
          const email = parts[1];
          return email; // Always send email for concatenated format
        }
      }
      
      // Find matching order to determine the type
      for (const customer of allCustomers) {
        // Check if it's a concatenated name-email format
        const nameAndEmail = `${customer?.firstName || ''} ${customer?.lastName || ''} - ${customer?.email || ''}`.trim();
        if (nameAndEmail === searchTerm && customer?.email) {
          return customer?.email; // Send email when searching by name-email format
        }
        
        // Check if it's an email
        if (customer?.email === searchTerm) {
          return searchTerm;
        }
        
        // Check if it's a phone number
        if (customer?.phoneNumber === searchTerm) {
          return searchTerm;
        }
      }
      
      // If no exact match found, return the search term as is
      return searchTerm;
    };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClear = () => {
    onClearFilters();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          padding: 0,
        },
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        '& .MuiDrawer-paper': {
          overflow: 'visible',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
          }}
        >
          <Typography variant="h6" component="h2">
            Apply Filters
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: 'white' }}
            size="small"
          >
            <FaTimes />
          </IconButton>
        </Box>

        {/* Filter Content */}
        <Box sx={{ flex: 1, padding: '20px', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Search Filter */}
            <Box>
              <Autocomplete
                freeSolo
                options={searchOptions}
                value={filters.search || ''}
                onInputChange={(event, newInputValue) => {
                  const searchValue = getSearchValue(newInputValue || '');
                  setSearchTerm(searchValue);
                  onFilterChange('search', searchValue);
                }}
                onChange={(event, newValue) => {
                  const searchValue = getSearchValue(newValue || '');
                  setSearchTerm(searchValue);
                  onFilterChange('search', searchValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Search Customer"
                    placeholder="Search by customer name, phone number or email"
                    onFocus={loadAllRecords}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event: React.SyntheticEvent) => {
                    const listboxNode = event.currentTarget;
                    if (
                      listboxNode.scrollTop + listboxNode.clientHeight >=
                      listboxNode.scrollHeight - 10
                    ) {
                      loadMoreData();
                    }
                  },
                  style: {
                    maxHeight: '200px',
                  },
                }}
                sx={{
                  '& .MuiAutocomplete-popper': {
                    zIndex: 9999,
                  },
                }}
                loading={customerLoading || isLoadingMore}
                loadingText={isLoadingMore ? "Loading more..." : "Loading suggestions..."}
                noOptionsText={hasMoreData ? "Keep typing to search..." : "No matching results"}
              />
            </Box>

            {/* Payment Status Filter */}
            <FormControl fullWidth size="small">
              <InputLabel shrink>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                displayEmpty
                onChange={(e) => onFilterChange('status', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      zIndex: 9999,
                    },
                  },
                  disablePortal: true,
                }}
              >
                <MenuItem value="all">All</MenuItem>
                  <MenuItem  value="active">
                    Active
                  </MenuItem>
                <MenuItem  value="inactive">
                    Inactive
                  </MenuItem>
                  <MenuItem  value="ban">
                    Ban
                  </MenuItem>
              </Select>
            </FormControl>
            
            {/* Date Range Filter (independent fields) */}
            <DateRangeFilter
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={onFilterChange}
              label="Joined Date Range"
              onValidityChange={setIsDateRangeValid}
            />

            
          </Box>
        </Box>

        <Divider />

        {/* Footer Actions */}
        <Box
          sx={{
            padding: '16px 20px',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{ flex: 1 }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!isDateRangeValid}
            sx={{ flex: 1 }}
          >
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CustomerSlidingFilter;
